import { useEffect, useState } from "react";

/**
 * Écran de démarrage Nold Go (PWA & mobile).
 * - Affiché uniquement au premier montage de l'app (dans la même session onglet).
 * - Fade-in du logo + pulse subtile, disparaît après ~1.4 s.
 * - Bloqué automatiquement sur bentskingo (ne s'affiche que sur noldgo ou en mode standalone).
 * - Utilise `sessionStorage` pour ne pas ré-apparaître à chaque route (une fois par session).
 */
export default function SplashScreen() {
  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    // Ne jamais afficher plusieurs fois par session onglet
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("nold_splash_shown") === "1") return;

    const host = (window.location.hostname || "").toLowerCase();
    const isNold = /noldgo\./i.test(host);
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator?.standalone === true;
    // Afficher uniquement sur noldgo (site + PWA) ou en preview (test)
    const isPreview = /emergentagent\.com$/i.test(host);
    if (!isNold && !isStandalone && !isPreview) return;

    setVisible(true);
    const t1 = setTimeout(() => setFadingOut(true), 1100);
    const t2 = setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem("nold_splash_shown", "1");
    }, 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-splash"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white transition-opacity duration-500 ${
        fadingOut ? "opacity-0" : "opacity-100"
      }`}
      aria-hidden="true"
    >
      <div className="splash-logo-wrap">
        <img
          src="/nold-logo.png?v=7"
          alt="Nold Go"
          className="splash-logo max-w-[80vw] max-h-[45vh] object-contain"
        />
      </div>
      <div className="mt-6 flex gap-1.5">
        <span className="splash-dot" style={{ animationDelay: "0s" }} />
        <span className="splash-dot" style={{ animationDelay: "0.15s" }} />
        <span className="splash-dot" style={{ animationDelay: "0.30s" }} />
      </div>

      <style>{`
        @keyframes splash-fade-scale {
          0%   { opacity: 0; transform: scale(0.94); }
          60%  { opacity: 1; transform: scale(1.02); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes splash-pulse {
          0%, 100% { transform: scale(1); opacity: 0.35; }
          50%      { transform: scale(1.4); opacity: 1; }
        }
        .splash-logo {
          animation: splash-fade-scale 750ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .splash-dot {
          width: 10px; height: 10px; border-radius: 999px; background: #E79643;
          display: inline-block;
          animation: splash-pulse 900ms ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
